import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { analyticsStyles } from '../../styles/analytics-styles.js';

export class AnalyticsDashboard extends LitElement {
  static properties = {
    timeRange: { type: String, state: true },
    agentFilter: { type: String, state: true },
    hoveredBar: { type: Number, state: true },
    hoveredHeatCell: { type: Object, state: true },
  };

  static styles = [designSystem, componentStyles, analyticsStyles];

  constructor() {
    super();
    this.timeRange = '7d';
    this.agentFilter = 'all';
    this.hoveredBar = -1;
    this.hoveredHeatCell = null;

    this._initMockData();
  }

  /* ───────────────────────────────────────────
     Mock Data
  ─────────────────────────────────────────── */
  _initMockData() {
    this._kpis = [
      {
        label: 'Total Calls',
        value: '2,847',
        trend: +12.3,
        icon: 'fa-phone',
        accent: 'var(--color-primary)',
        bg: 'var(--color-primary-light)',
        sparkHeights: [40, 55, 48, 70, 62, 85, 78],
      },
      {
        label: 'Answer Rate',
        value: '94.2%',
        trend: +2.1,
        icon: 'fa-phone-arrow-up-right',
        accent: 'var(--color-success)',
        bg: 'var(--color-success-light)',
        sparkHeights: [70, 68, 75, 80, 78, 88, 94],
      },
      {
        label: 'Avg Handle Time',
        value: '4:32',
        trend: -5.4,
        icon: 'fa-stopwatch',
        accent: 'var(--color-accent)',
        bg: 'var(--color-accent-light)',
        sparkHeights: [82, 78, 72, 65, 68, 58, 52],
      },
      {
        label: 'CSAT',
        value: '4.7 / 5',
        trend: +3.2,
        icon: 'fa-face-smile',
        accent: 'var(--color-info)',
        bg: 'var(--color-info-light)',
        sparkHeights: [60, 65, 72, 70, 78, 82, 90],
      },
      {
        label: 'First Call Resolution',
        value: '87.5%',
        trend: +4.8,
        icon: 'fa-bullseye-arrow',
        accent: '#06B6D4',
        bg: '#ECFEFF',
        sparkHeights: [55, 62, 70, 68, 75, 82, 88],
      },
      {
        label: 'Conversion Rate',
        value: '23.4%',
        trend: +8.1,
        icon: 'fa-arrow-trend-up',
        accent: '#D946EF',
        bg: '#FDF4FF',
        sparkHeights: [30, 38, 42, 50, 55, 62, 70],
      },
    ];

    // Call volume: 24 bars
    this._callVolume = Array.from({ length: 24 }, (_, i) => {
      // Simulate realistic pattern: low at night, peaks in afternoon
      const base = i < 6 ? 8 : i < 9 ? 25 : i < 12 ? 55 : i < 15 ? 70 : i < 18 ? 60 : i < 21 ? 40 : 15;
      return {
        hour: i,
        label: i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`,
        calls: base + Math.floor(Math.random() * 20),
      };
    });
    this._callVolumeMax = Math.max(...this._callVolume.map(d => d.calls));

    // Funnel
    this._funnel = [
      { stage: 'Calls Received', count: 2847, pct: 100 },
      { stage: 'Answered', count: 2682, pct: 94.2 },
      { stage: 'Qualified', count: 1876, pct: 65.9 },
      { stage: 'Reservation Made', count: 667, pct: 23.4 },
      { stage: 'Payment Completed', count: 534, pct: 18.8 },
    ];

    // Heatmap: 7 days x 24 hours
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    this._heatmap = days.map(day => ({
      day,
      hours: Array.from({ length: 24 }, (_, h) => {
        // Realistic: low at night, high mid-day, weekends slightly different
        const isWeekend = day === 'Sat' || day === 'Sun';
        const base = h < 6 ? 0 : h < 9 ? (isWeekend ? 1 : 2) : h < 12 ? (isWeekend ? 3 : 4) : h < 15 ? 5 : h < 18 ? (isWeekend ? 4 : 3) : h < 21 ? 2 : 1;
        const jitter = Math.floor(Math.random() * 2);
        const intensity = Math.min(5, Math.max(0, base + jitter - 1));
        const calls = intensity * 8 + Math.floor(Math.random() * 10);
        return { hour: h, intensity, calls };
      }),
    }));

    // Performance metrics
    this._performance = [
      { label: 'Agent Utilization', value: '87%', pct: 87, color: 'var(--color-primary)' },
      { label: 'Quality Score', value: '92%', pct: 92, color: 'var(--color-success)' },
      { label: 'CSAT', value: '94%', pct: 94, color: 'var(--color-accent)' },
      { label: 'Resolution Rate', value: '88%', pct: 88, color: 'var(--color-info)' },
    ];
  }

  /* ───────────────────────────────────────────
     Handlers
  ─────────────────────────────────────────── */
  _setTimeRange(range) {
    this.timeRange = range;
  }

  _onAgentChange(e) {
    this.agentFilter = e.target.value;
  }

  _refresh() {
    // Re-generate some mock jitter for demo
    this._callVolume = this._callVolume.map(d => ({
      ...d,
      calls: d.calls + Math.floor(Math.random() * 10) - 5,
    }));
    this._callVolumeMax = Math.max(...this._callVolume.map(d => d.calls));
    this.requestUpdate();
  }

  /* ───────────────────────────────────────────
     Render
  ─────────────────────────────────────────── */
  render() {
    return html`
      ${iconStyles}
      <div class="dashboard-layout">

        <!-- Time Range Selector Row -->
        <div class="controls-row">
          <div class="controls-left">
            <div class="time-pills">
              ${['24h', '7d', '30d', '90d'].map(r => html`
                <button
                  class="time-pill ${this.timeRange === r ? 'active' : ''}"
                  @click="${() => this._setTimeRange(r)}"
                >${r.toUpperCase()}</button>
              `)}
            </div>

            <select class="agent-select" .value="${this.agentFilter}" @change="${this._onAgentChange}">
              <option value="all">All Agents</option>
              <option value="ava">Ava (AI)</option>
              <option value="noah">Noah (AI)</option>
              <option value="mia">Mia (AI)</option>
              <option value="liam">Liam (AI)</option>
            </select>
          </div>

          <button class="refresh-btn" @click="${this._refresh}" title="Refresh data">
            <i class="fa-sharp fa-light fa-arrows-rotate"></i>
          </button>
        </div>

        <!-- 6 KPI Cards -->
        <div class="kpi-grid">
          ${this._kpis.map(kpi => this._renderKPI(kpi))}
        </div>

        <!-- Call Volume Trend Chart -->
        <div class="section-card">
          <div class="section-header">
            <div class="section-title-group">
              <span class="section-title">Call Volume Trend</span>
              <span class="section-subtitle">Hourly call distribution for selected period</span>
            </div>
          </div>
          ${this._renderBarChart()}
        </div>

        <!-- Two-column: Funnel + Heatmap -->
        <div class="two-col">
          <!-- Conversion Funnel -->
          <div class="section-card">
            <div class="section-header">
              <div class="section-title-group">
                <span class="section-title">Conversion Funnel</span>
                <span class="section-subtitle">Call-to-payment pipeline</span>
              </div>
            </div>
            ${this._renderFunnel()}
          </div>

          <!-- Peak Hours Heatmap -->
          <div class="section-card">
            <div class="section-header">
              <div class="section-title-group">
                <span class="section-title">Peak Hours Heatmap</span>
                <span class="section-subtitle">Call intensity by day and hour</span>
              </div>
            </div>
            ${this._renderHeatmap()}
          </div>
        </div>

        <!-- Performance Breakdown Row -->
        <div class="section-card">
          <div class="section-header">
            <div class="section-title-group">
              <span class="section-title">Performance Breakdown</span>
              <span class="section-subtitle">Key operational metrics</span>
            </div>
          </div>
          <div class="perf-row">
            ${this._performance.map(m => this._renderPerfCard(m))}
          </div>
        </div>

      </div>
    `;
  }

  /* ───────────────────────────────────────────
     KPI Card
  ─────────────────────────────────────────── */
  _renderKPI(kpi) {
    const isUp = kpi.trend >= 0;
    return html`
      <div class="kpi-card" style="--kpi-accent: ${kpi.accent}; --kpi-bg: ${kpi.bg};">
        <div class="kpi-top">
          <div class="kpi-icon">
            <i class="fa-sharp-duotone fa-solid ${kpi.icon}"></i>
          </div>
          <span class="kpi-trend ${isUp ? 'up' : 'down'}">
            <i class="fa-sharp fa-light ${isUp ? 'fa-arrow-up' : 'fa-arrow-down'}" style="font-size: 9px;"></i>
            ${Math.abs(kpi.trend)}%
          </span>
        </div>
        <span class="kpi-value">${kpi.value}</span>
        <span class="kpi-label">${kpi.label}</span>
        <div class="kpi-sparkline">
          ${kpi.sparkHeights.map(h => html`
            <div class="spark-bar" style="height: ${h}%;"></div>
          `)}
        </div>
      </div>
    `;
  }

  /* ───────────────────────────────────────────
     Bar Chart
  ─────────────────────────────────────────── */
  _renderBarChart() {
    const max = this._callVolumeMax;
    return html`
      <div class="bar-chart">
        <div class="bar-chart-area">
          ${this._callVolume.map(d => {
            const pct = max > 0 ? (d.calls / max) * 100 : 0;
            return html`
              <div class="bar-column">
                <div class="bar-tooltip">${d.calls} calls</div>
                <div class="bar-fill" style="height: ${pct}%;"></div>
              </div>
            `;
          })}
        </div>
        <div class="bar-chart-x-axis">
          ${this._callVolume.map((d, i) => html`
            <span class="x-label ${i % 3 === 0 ? 'highlight' : ''}">${i % 2 === 0 ? d.label : ''}</span>
          `)}
        </div>
      </div>
    `;
  }

  /* ───────────────────────────────────────────
     Conversion Funnel
  ─────────────────────────────────────────── */
  _renderFunnel() {
    const maxCount = this._funnel[0].count;
    return html`
      <div class="funnel-stages">
        ${this._funnel.map((stage, idx) => {
          const widthPct = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
          return html`
            ${idx > 0 ? html`
              <div class="funnel-connector">
                <i class="fa-sharp fa-light fa-chevron-down"></i>
              </div>
            ` : ''}
            <div class="funnel-stage">
              <div class="funnel-bar-wrap">
                <div class="funnel-bar" style="width: ${widthPct}%;">
                  <span class="funnel-stage-label">${stage.stage}</span>
                  <span class="funnel-stage-count">${stage.count.toLocaleString()}</span>
                </div>
              </div>
              <span class="funnel-pct">${stage.pct}%</span>
            </div>
          `;
        })}
      </div>
    `;
  }

  /* ───────────────────────────────────────────
     Peak Hours Heatmap
  ─────────────────────────────────────────── */
  _renderHeatmap() {
    const hours = Array.from({ length: 24 }, (_, i) =>
      i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i - 12}p`
    );

    return html`
      <div class="heatmap-container">
        <div class="heatmap-grid">
          <!-- Column headers -->
          <div class="heatmap-corner"></div>
          ${hours.map(h => html`<div class="heatmap-col-header">${h}</div>`)}

          <!-- Rows -->
          ${this._heatmap.map(row => html`
            <div class="heatmap-row-label">${row.day}</div>
            ${row.hours.map(cell => html`
              <div class="heat-cell i${cell.intensity}" title="${row.day} ${hours[cell.hour]}: ${cell.calls} calls">
                <span class="heat-cell-tooltip">${cell.calls}</span>
              </div>
            `)}
          `)}
        </div>

        <!-- Legend -->
        <div class="heatmap-legend">
          <span class="legend-label">Low</span>
          <div class="legend-scale">
            <div class="legend-block" style="background: var(--color-bg-tertiary);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.12);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.28);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.45);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.65);"></div>
            <div class="legend-block" style="background: rgba(45, 143, 111, 0.85);"></div>
          </div>
          <span class="legend-label">High</span>
        </div>
      </div>
    `;
  }

  /* ───────────────────────────────────────────
     Performance Card
  ─────────────────────────────────────────── */
  _renderPerfCard(m) {
    return html`
      <div class="perf-card">
        <div class="perf-header">
          <span class="perf-label">${m.label}</span>
        </div>
        <span class="perf-value">${m.value}</span>
        <div class="perf-bar-track">
          <div class="perf-bar-fill" style="width: ${m.pct}%; background: ${m.color};"></div>
        </div>
      </div>
    `;
  }
}

customElements.define('analytics-dashboard', AnalyticsDashboard);
